import {
  ETAPAS_XABCDE,
  cenarioExtracaoPorId,
  type AprisionamentoCenario,
  type CenarioExtracao,
  type EtapaDecisaoExtracao,
  type EtapaXabcde,
  type ModoExperienciaExtracao,
} from './cenariosExtracao'
import type { RegiaoAnatomica } from './regiaoAnatomica'

export type TipoDecisaoExtracao =
  | 'cenario-iniciado'
  | 'foco-vitima'
  | 'triagem'
  | 'xabcde'
  | 'justificativa'
  | 'prontidao'
  | 'procedimento'
  | 'estrategia'
  | 'retirada'
  | 'classificacao'
  | 'atencao-inicial'
  | 'debriefing-liberado'

/** Usado pela serialização para validar `DecisaoExtracao.tipo` sem duplicar a lista literal. */
export const TIPOS_DECISAO_EXTRACAO: readonly TipoDecisaoExtracao[] = [
  'cenario-iniciado',
  'foco-vitima',
  'triagem',
  'xabcde',
  'justificativa',
  'prontidao',
  'procedimento',
  'estrategia',
  'retirada',
  'classificacao',
  'atencao-inicial',
  'debriefing-liberado',
]

/**
 * Escala genérica de trabalho do ALUNO — não é uma categoria oficial de
 * nenhum protocolo (PHTLS/START/SALT). Serve só pra registrar a leitura do
 * aluno naquele momento e comparar depois com a referência do professor.
 * "dadosInsuficientes" cobre o "declarar dados insuficientes e buscar nova
 * informação" do plano, sem inventar um mecanismo à parte.
 */
export type PrioridadeDeclarada = 'alta' | 'moderada' | 'baixa' | 'dadosInsuficientes'
export const PRIORIDADES_DECLARADAS: readonly PrioridadeDeclarada[] = ['alta', 'moderada', 'baixa', 'dadosInsuficientes']

export interface DeclaracaoClassificacao {
  prioridade: PrioridadeDeclarada
  justificativa: string
}

/** Quem o aluno decide que recebe avaliação/intervenção primeiro — distinto da ordem física de retirada. */
export interface AtencaoInicialExtracao {
  vitimaId: string | null
  justificativa: string
}

export interface DecisaoExtracao {
  id: string
  ordem: number
  instanteMs: number
  tipo: TipoDecisaoExtracao
  etapa: EtapaDecisaoExtracao
  vitimaId?: string
  veiculoId?: string
  titulo: string
  descricao: string
  justificativa?: string
  consequencias?: readonly string[]
}

export interface AvaliacaoVitimaExtracao {
  etapasAvaliadas: readonly EtapaXabcde[]
}

export interface ProntidaoExtracao {
  veiculoEstabilizado: boolean
  riscosGerenciados: boolean
  acessoVitima: boolean
  procedimentosInternos: 'pendente' | 'parcial' | 'concluido'
  rotaDisponivel: boolean
  pacientePronto: 'avaliar' | 'sim' | 'nao'
}

export interface SessaoExtracao {
  modo: ModoExperienciaExtracao
  cenario: CenarioExtracao
  etapa: EtapaDecisaoExtracao
  vitimaFocoId: string
  avaliacoes: Readonly<Record<string, AvaliacaoVitimaExtracao>>
  ordemRetirada: readonly string[]
  justificativas: Readonly<Partial<Record<EtapaDecisaoExtracao, string>>>
  decisoes: readonly DecisaoExtracao[]
  prontidao: ProntidaoExtracao
  retiradaIniciada: boolean
  /** decisão 2 do plano (Fase 5B): a leitura do PRÓPRIO aluno, por vítima */
  classificacoesAluno: Readonly<Record<string, DeclaracaoClassificacao>>
  /** decisão 3 do plano: quem recebe avaliação/intervenção primeiro */
  atencaoInicial: AtencaoInicialExtracao
  /** referência do professor só aparece no debriefing depois desta liberação */
  debriefingLiberado: boolean
}

const avaliacaoVazia = (): AvaliacaoVitimaExtracao => ({ etapasAvaliadas: [] })

const prontidaoInicial = (cenario: CenarioExtracao): ProntidaoExtracao => {
  const veiculo = cenario.veiculos[0]!
  return {
    veiculoEstabilizado: veiculo.estabilidadeInicial,
    riscosGerenciados: veiculo.riscosIniciais.length === 0,
    acessoVitima: veiculo.acessoInicial === 'livre',
    procedimentosInternos: 'pendente',
    rotaDisponivel: veiculo.acessoInicial === 'livre',
    pacientePronto: 'avaliar',
  }
}

const proximaDecisao = (
  sessao: SessaoExtracao,
  decisao: Omit<DecisaoExtracao, 'id' | 'ordem' | 'instanteMs'>,
): DecisaoExtracao => ({
  ...decisao,
  ordem: sessao.decisoes.length + 1,
  id: `decisao-${sessao.decisoes.length + 1}`,
  instanteMs: Date.now(),
})

export function criarSessaoExtracao(
  modo: ModoExperienciaExtracao = 'demonstracao',
  cenarioId = 'motorista-estavel',
  /** cenários fora do catálogo fixo — hoje, cenários autorais do professor (Fase 5B) */
  extras: readonly CenarioExtracao[] = [],
): SessaoExtracao {
  const cenario = cenarioExtracaoPorId(cenarioId, extras)
  const vitimaFocoId = cenario.vitimas[0]!.id
  const base: SessaoExtracao = {
    modo,
    cenario,
    etapa: 'porque',
    vitimaFocoId,
    avaliacoes: Object.fromEntries(cenario.vitimas.map((v) => [v.id, avaliacaoVazia()])),
    ordemRetirada: [],
    justificativas: {},
    decisoes: [],
    prontidao: prontidaoInicial(cenario),
    retiradaIniciada: false,
    classificacoesAluno: {},
    atencaoInicial: { vitimaId: null, justificativa: '' },
    debriefingLiberado: false,
  }
  return modo === 'demonstracao'
    ? base
    : {
        ...base,
        decisoes: [proximaDecisao(base, {
          tipo: 'cenario-iniciado', etapa: 'porque', titulo: 'Cenário iniciado', descricao: cenario.nome,
        })],
      }
}

/** Alternar modo preserva o cenário e suas escolhas; demonstração apenas ignora a progressão clínica. */
export function trocarModoExtracao(sessao: SessaoExtracao, modo: ModoExperienciaExtracao): SessaoExtracao {
  return { ...sessao, modo }
}

export function trocarCenarioExtracao(
  sessao: SessaoExtracao,
  cenarioId: string,
  extras: readonly CenarioExtracao[] = [],
): SessaoExtracao {
  return criarSessaoExtracao(sessao.modo, cenarioId, extras)
}

export function selecionarVitimaSessao(sessao: SessaoExtracao, vitimaId: string): SessaoExtracao {
  const vitima = sessao.cenario.vitimas.find((item) => item.id === vitimaId)
  if (!vitima) return sessao
  const decisao = proximaDecisao(sessao, {
    tipo: sessao.cenario.vitimas.length > 1 ? 'triagem' : 'foco-vitima',
    etapa: 'porque',
    vitimaId,
    veiculoId: vitima.veiculoId,
    titulo: `${vitima.nome} selecionada`,
    descricao: sessao.cenario.vitimas.length > 1 ? 'Escolha registrada para discussão de prioridade.' : 'Vítima em avaliação.',
  })
  return { ...sessao, vitimaFocoId: vitimaId, decisoes: [...sessao.decisoes, decisao] }
}

export function avaliarEtapaXabcde(
  sessao: SessaoExtracao,
  etapa: EtapaXabcde,
  vitimaId = sessao.vitimaFocoId,
): SessaoExtracao {
  if (sessao.modo === 'demonstracao') return sessao
  const vitima = sessao.cenario.vitimas.find((item) => item.id === vitimaId)
  if (!vitima) return sessao
  const atual = sessao.avaliacoes[vitimaId] ?? avaliacaoVazia()
  if (atual.etapasAvaliadas.includes(etapa)) return sessao
  const avaliacao = { etapasAvaliadas: [...atual.etapasAvaliadas, etapa] }
  const achado = vitima.xabcde[etapa]
  const decisao = proximaDecisao(sessao, {
    tipo: 'xabcde', etapa: 'porque', vitimaId, veiculoId: vitima.veiculoId,
    titulo: `${etapa}: ${achado.resumo}`, descricao: achado.detalhe,
  })
  return {
    ...sessao,
    avaliacoes: { ...sessao.avaliacoes, [vitimaId]: avaliacao },
    decisoes: [...sessao.decisoes, decisao],
  }
}

export const achadosRevelados = (sessao: SessaoExtracao, vitimaId = sessao.vitimaFocoId) => {
  const vitima = sessao.cenario.vitimas.find((item) => item.id === vitimaId)
  const avaliadas = new Set(sessao.avaliacoes[vitimaId]?.etapasAvaliadas ?? [])
  return vitima
    ? ETAPAS_XABCDE.filter((etapa) => avaliadas.has(etapa)).map((etapa) => ({ etapa, ...vitima.xabcde[etapa] }))
    : []
}

/**
 * Só os achados revelados que têm uma região anatômica associada — o que a
 * cena 3D usa para destacar. Um achado sem `regiao` (ex.: "sem achados
 * relevantes") continua só no painel, sem destaque na maquete: destacar uma
 * região por um achado que não indica nada ali seria o próprio vazamento que
 * a Fase 0 corrigiu, só que na cena em vez do texto.
 */
export function achadosComRegiaoVisiveis(
  sessao: SessaoExtracao,
  vitimaId = sessao.vitimaFocoId,
): ReadonlyArray<{ etapa: EtapaXabcde; resumo: string; detalhe: string; regiao: RegiaoAnatomica }> {
  return achadosRevelados(sessao, vitimaId).filter(
    (achado): achado is typeof achado & { regiao: RegiaoAnatomica } => achado.regiao != null,
  )
}

/**
 * Aprisionamentos só aparecem — painel E cena 3D — depois que a exposição do
 * corpo (E) foi avaliada, a etapa que de fato examina o que prende a vítima.
 * Centraliza um gate que antes era recalculado igual em dois lugares
 * (`PainelDecisaoExtracao` e a montagem da cena em `App.tsx`).
 */
export function aprisionamentosVisiveis(
  sessao: SessaoExtracao,
  vitimaId = sessao.vitimaFocoId,
): readonly AprisionamentoCenario[] {
  const vitima = sessao.cenario.vitimas.find((item) => item.id === vitimaId)
  if (!vitima) return []
  const avaliadas = sessao.avaliacoes[vitimaId]?.etapasAvaliadas ?? []
  return avaliadas.includes('E') ? vitima.aprisionamentos : []
}

/**
 * A vítima já recebeu o suficiente do XABCDE para a severidade clínica dela
 * (estado, prioridade) aparecer fora dos achados — painel, inspetor, rótulo 3D.
 *
 * Exigir as 6 etapas (não "qualquer uma") é o que faz a leitura ter relação
 * com a evidência avaliada, em vez de vazar a prioridade inteira porque o
 * aluno clicou em E (o achado tipicamente mais brando). Achado da auditoria
 * da Fase 0 — ver docs/fase-0-auditoria.md, seção 4.
 *
 * Em demonstração não há gate: o modo é livre de exigência clínica, então
 * severidade sempre pode aparecer.
 */
export function vitimaSeveridadeVisivel(sessao: SessaoExtracao, vitimaId: string): boolean {
  if (sessao.modo === 'demonstracao') return true
  return (sessao.avaliacoes[vitimaId]?.etapasAvaliadas.length ?? 0) === ETAPAS_XABCDE.length
}

export function registrarJustificativa(
  sessao: SessaoExtracao,
  etapa: EtapaDecisaoExtracao,
  justificativa: string,
): SessaoExtracao {
  const texto = justificativa.trim()
  if (!texto) return sessao
  const decisao = proximaDecisao(sessao, {
    tipo: 'justificativa', etapa, vitimaId: sessao.vitimaFocoId,
    titulo: 'Justificativa registrada', descricao: texto, justificativa: texto,
  })
  return { ...sessao, justificativas: { ...sessao.justificativas, [etapa]: texto }, decisoes: [...sessao.decisoes, decisao] }
}

export function atualizarProntidao(
  sessao: SessaoExtracao,
  alteracao: Partial<ProntidaoExtracao>,
  descricao: string,
): SessaoExtracao {
  const prontidao = { ...sessao.prontidao, ...alteracao }
  const decisao = proximaDecisao(sessao, {
    tipo: 'prontidao', etapa: 'quando', vitimaId: sessao.vitimaFocoId,
    titulo: 'Prontidão atualizada', descricao,
  })
  return { ...sessao, prontidao, decisoes: [...sessao.decisoes, decisao] }
}

export function decidirMomentoRetirada(sessao: SessaoExtracao, retirarAgora: boolean): SessaoExtracao {
  const pendencias = Object.entries(sessao.prontidao)
    .filter(([, valor]) => valor === false || valor === 'pendente' || valor === 'nao')
    .map(([chave]) => chave)
  const consequencias = retirarAgora && pendencias.length > 0
    ? [`Retirada iniciada com pendências: ${pendencias.join(', ')}.`]
    : retirarAgora
      ? ['A preparação registrada favorece o início da movimentação.']
      : ['A equipe optou por preparar mais antes da movimentação.']
  const decisao = proximaDecisao(sessao, {
    tipo: 'prontidao', etapa: 'quando', vitimaId: sessao.vitimaFocoId,
    titulo: retirarAgora ? 'Retirar agora' : 'Preparar mais',
    descricao: retirarAgora ? 'O aluno decidiu iniciar mesmo podendo haver pendências.' : 'O aluno decidiu continuar a preparação.',
    consequencias,
  })
  return {
    ...sessao,
    retiradaIniciada: retirarAgora,
    etapa: retirarAgora ? 'como' : 'quando',
    decisoes: [...sessao.decisoes, decisao],
  }
}

export function registrarOrdemRetirada(sessao: SessaoExtracao, vitimaId: string, justificativa = ''): SessaoExtracao {
  if (!sessao.cenario.vitimas.some((v) => v.id === vitimaId) || sessao.ordemRetirada.includes(vitimaId)) return sessao
  const vitima = sessao.cenario.vitimas.find((v) => v.id === vitimaId)!
  const decisao = proximaDecisao(sessao, {
    tipo: 'retirada', etapa: 'como', vitimaId, veiculoId: vitima.veiculoId,
    titulo: `${vitima.nome} escolhida para retirada`,
    descricao: `Ordem ${sessao.ordemRetirada.length + 1}, sem bloqueio automático por prioridade clínica.`,
    justificativa: justificativa.trim() || undefined,
  })
  return { ...sessao, ordemRetirada: [...sessao.ordemRetirada, vitimaId], decisoes: [...sessao.decisoes, decisao] }
}

/**
 * Decisão 2 do plano (Fase 5B): a leitura de prioridade do PRÓPRIO aluno,
 * distinta de `urgencia` (o que o catálogo/professor definiu) e de
 * `vitimaSeveridadeVisivel` (quando a severidade real pode aparecer). Sem
 * exigir XABCDE completo antes — o aluno pode declarar com o que já viu, ou
 * "dadosInsuficientes". Chamar de novo substitui a leitura anterior, mas
 * cada chamada continua registrada em `decisoes`: o histórico preserva o
 * que era conhecido em cada momento, não só o valor final.
 */
export function declararClassificacao(
  sessao: SessaoExtracao,
  vitimaId: string,
  prioridade: PrioridadeDeclarada,
  justificativa: string,
): SessaoExtracao {
  const vitima = sessao.cenario.vitimas.find((item) => item.id === vitimaId)
  if (!vitima) return sessao
  const decisao = proximaDecisao(sessao, {
    tipo: 'classificacao', etapa: 'porque', vitimaId, veiculoId: vitima.veiculoId,
    titulo: `${vitima.nome}: prioridade declarada como ${prioridade}`,
    descricao: justificativa.trim() || 'Sem justificativa escrita.',
    justificativa: justificativa.trim() || undefined,
  })
  return {
    ...sessao,
    classificacoesAluno: { ...sessao.classificacoesAluno, [vitimaId]: { prioridade, justificativa: justificativa.trim() } },
    decisoes: [...sessao.decisoes, decisao],
  }
}

/** Decisão 3 do plano: quem recebe avaliação/intervenção primeiro — não é a ordem física de retirada. */
export function declararAtencaoInicial(
  sessao: SessaoExtracao,
  vitimaId: string,
  justificativa: string,
): SessaoExtracao {
  const vitima = sessao.cenario.vitimas.find((item) => item.id === vitimaId)
  if (!vitima) return sessao
  const decisao = proximaDecisao(sessao, {
    tipo: 'atencao-inicial', etapa: 'porque', vitimaId, veiculoId: vitima.veiculoId,
    titulo: `${vitima.nome} recebe atenção primeiro`,
    descricao: justificativa.trim() || 'Sem justificativa escrita.',
    justificativa: justificativa.trim() || undefined,
  })
  return {
    ...sessao,
    atencaoInicial: { vitimaId, justificativa: justificativa.trim() },
    decisoes: [...sessao.decisoes, decisao],
  }
}

/**
 * Libera a referência do professor no debriefing (Fase 5B). Ação do
 * instrutor, não do aluno — a UI precisa deixar isso claro, já que o app não
 * tem conta/papel de usuário. Antes desta chamada, `compararComReferencia`
 * pode ser calculado, mas nenhuma tela deve mostrar o resultado.
 */
export function liberarDebriefing(sessao: SessaoExtracao): SessaoExtracao {
  if (sessao.debriefingLiberado) return sessao
  const decisao = proximaDecisao(sessao, {
    tipo: 'debriefing-liberado', etapa: 'debriefing',
    titulo: 'Referência do professor liberada', descricao: 'O debriefing agora mostra a referência de correção.',
  })
  return { ...sessao, debriefingLiberado: true, decisoes: [...sessao.decisoes, decisao] }
}

export function navegarEtapaExtracao(sessao: SessaoExtracao, etapa: EtapaDecisaoExtracao): SessaoExtracao {
  return { ...sessao, etapa }
}

export interface DebriefingExtracao {
  linhaDoTempo: readonly DecisaoExtracao[]
  favoreceu: readonly string[]
  dificultou: readonly string[]
  alternativas: readonly string[]
  discussao: readonly string[]
}

export function gerarDebriefing(sessao: SessaoExtracao): DebriefingExtracao {
  const vitima = sessao.cenario.vitimas.find((item) => item.id === sessao.vitimaFocoId)
  const avaliadas = sessao.avaliacoes[sessao.vitimaFocoId]?.etapasAvaliadas.length ?? 0
  const primeira = sessao.ordemRetirada[0]
  const prioritarias = sessao.cenario.vitimas.filter((item) => item.urgencia === 'maior')
  return {
    linhaDoTempo: sessao.decisoes,
    favoreceu: [
      avaliadas > 0 ? `${avaliadas} etapa(s) do XABCDE informaram a decisão.` : 'A demonstração preservou acesso direto às técnicas.',
      sessao.prontidao.rotaDisponivel ? 'A rota estava disponível.' : 'A escolha explicitou a necessidade de criação de espaço.',
    ],
    dificultou: [
      ...sessao.cenario.veiculos.flatMap((veiculo) => veiculo.riscosIniciais),
      ...(vitima?.aprisionamentos.map((item) => item.descricao) ?? []),
    ],
    alternativas: sessao.cenario.vitimas.length > 1
      ? sessao.cenario.vitimas.filter((item) => item.id !== primeira).map((item) => `Avaliar/retirar ${item.nome} em outra sequência.`)
      : ['Comparar outra abertura e observar os trade-offs de giro, folga e obstáculos.'],
    discussao: [
      primeira && prioritarias.length > 0 && !prioritarias.some((item) => item.id === primeira)
        ? 'A primeira retirada não foi a de maior prioridade clínica. O ganho operacional justificou a sequência?'
        : 'Como a urgência clínica influenciou — sem determinar sozinha — a estratégia?',
      'Que consequência mudaria se uma preparação fosse desfeita e a decisão repetida?',
    ],
  }
}
